/** SFU msid track id: `{shortCallId}-{participantNumber}-{video|screen|audio}` */
const SFU_TRACK_ID_PATTERN = /^[^-]+-\d+-(?:video|screen|audio)$/i

/** Firefox (and some others) use a random UUID for MediaStreamTrack.id; map it to the SFU msid. */
const mediaTrackIdAliases = new Map<string, string>()

export const isSfuTrackId = (id: string): boolean => SFU_TRACK_ID_PATTERN.test(id)

export const registerMediaTrackIdAlias = (browserTrackId: string, sfuTrackId: string): void => {
  if (!browserTrackId || !sfuTrackId || browserTrackId === sfuTrackId) {
    return
  }
  if (!isSfuTrackId(sfuTrackId)) {
    return
  }
  mediaTrackIdAliases.set(browserTrackId, sfuTrackId)
}

export const clearMediaTrackIdAlias = (browserTrackId: string): void => {
  mediaTrackIdAliases.delete(browserTrackId)
}

/** Test helper / session teardown */
export const clearAllMediaTrackIdAliases = (): void => {
  mediaTrackIdAliases.clear()
}

export const resolveMediaTrackId = (id: string): string => mediaTrackIdAliases.get(id) ?? id

/**
 * Extract the track part of `a=msid:<streamId> <trackId>` (or ssrc msid) for a given m-line mid.
 * Chrome usually copies trackId into MediaStreamTrack.id; Firefox keeps a UUID instead.
 */
export const getMsidTrackIdByMid = (sdp: string, mid: string): string | undefined => {
  if (!sdp || !mid) {
    return undefined
  }

  const sections = sdp.split(/(?=^m=)/m)
  for (const section of sections) {
    if (!section.startsWith('m=')) {
      continue
    }

    const midMatch = section.match(/^a=mid:(.+)$/m)
    if (midMatch?.[1] !== mid) {
      continue
    }

    const msid = section.match(/^a=msid:(\S+)\s+(\S+)\s*$/m)
    if (msid?.[2]) {
      return msid[2]
    }

    const ssrcMsid = section.match(/^a=ssrc:\d+ msid:(\S+)\s+(\S+)\s*$/m)
    if (ssrcMsid?.[2]) {
      return ssrcMsid[2]
    }
  }

  return undefined
}

type TrackEventLike = {
  track: MediaStreamTrack
  transceiver?: RTCRtpTransceiver
}

/**
 * Resolve SFU track id for an inbound RTCTrackEvent.
 * Prefer track.id when it already is SFU-shaped; otherwise read msid from remote SDP by mid.
 */
export const resolveSfuTrackIdFromTrackEvent = (
  peerConnection: RTCPeerConnection,
  event: TrackEventLike,
): string | undefined => {
  const { track } = event
  if (isSfuTrackId(track.id)) {
    return track.id
  }

  const mid = event.transceiver?.mid
    ?? peerConnection.getTransceivers().find((t) => t.receiver.track === track)?.mid

  if (!mid) {
    return undefined
  }

  const sdp = peerConnection.remoteDescription?.sdp
  if (!sdp) {
    return undefined
  }

  const fromSdp = getMsidTrackIdByMid(sdp, mid)
  if (fromSdp && isSfuTrackId(fromSdp)) {
    return fromSdp
  }

  return undefined
}

export function getVolume (dataArray: Float32Array) {
  const magicNumber = 200
  let sumSquares = 0.0
  for (const amplitude of dataArray) { sumSquares += amplitude * amplitude }
  return Math.sqrt(sumSquares / dataArray.length) * magicNumber
}

export const getParticipantShortCallIdFromTrackId = (id: string) => resolveMediaTrackId(id).split('-').at(0)

export const getParticipantNumberFromTrackId = (id: string) => resolveMediaTrackId(id).split('-').at(1)

export const getParticipantNumberFromStreamId = (streamId: MediaStream['id']) => streamId.split('-').at(1)

export const getTrackNameFromTrackId = (id: string) => resolveMediaTrackId(id).split('-').at(2)
